import { useLingui } from "@lingui/react/macro";
import type { ThreadMessage } from "@rakazo/contracts";
import { Button } from "@rakazo/ui-web";
import { LoaderCircle, Paperclip } from "lucide-react";
import type { PendingAttachment } from "./chat-shared";

export interface OutgoingMessage {
  nonce: string;
  threadKey: string;
  text: string;
  attachments: PendingAttachment[];
  status: "sending" | "failed" | "accepted";
  confirmedSeq?: number;
  retry: () => void;
}

/** Hide only the exact files already in flight. / Приховує лише конкретні файли, що надсилаються. */
export function attachmentsNotBeingSent(
  attachments: readonly PendingAttachment[],
  outgoing: readonly OutgoingMessage[],
): PendingAttachment[] {
  const sendingIds = new Set(
    outgoing
      .filter((message) => message.status === "sending")
      .flatMap((message) => message.attachments.map((attachment) => attachment.id)),
  );
  return attachments.filter((attachment) => !sendingIds.has(attachment.id));
}

/** A receipt replaces the local row only once history contains it. / Квитанція замінює локальну репліку після появи в історії. */
export function outgoingMessageIsConfirmed(
  message: Pick<OutgoingMessage, "nonce" | "confirmedSeq">,
  history: readonly ThreadMessage[],
): boolean {
  return history.some(
    (item) =>
      item.role === "user" &&
      (("clientNonce" in item && item.clientNonce === message.nonce) ||
        (message.confirmedSeq !== undefined && item.seq === message.confirmedSeq)),
  );
}

export function OutgoingMessageView({
  message,
  retryDisabled = false,
}: {
  message: OutgoingMessage;
  retryDisabled?: boolean;
}) {
  const { t, i18n } = useLingui();
  const fileSize = (size: number) => {
    const [divisor, unit]: [number, string] =
      size >= 1_000_000
        ? [1_000_000, "megabyte"]
        : size >= 1_000
          ? [1_000, "kilobyte"]
          : [1, "byte"];
    return new Intl.NumberFormat(i18n.locale || "en", {
      style: "unit",
      unit,
      unitDisplay: "short",
      maximumFractionDigits: 1,
    }).format(size / divisor);
  };
  return (
    <div className="flex justify-end" data-testid="outgoing-message">
      <div className="flex min-w-0 max-w-[84%] flex-col gap-2 rounded-[20px] bg-chat-user px-[18px] py-3 text-base leading-[1.45] text-chat-user-foreground">
        <div
          className="flex min-w-0 flex-col gap-2"
          aria-busy={message.status === "sending" || undefined}
        >
          {message.text ? (
            <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]" dir="auto">
              {message.text}
            </p>
          ) : null}
          {message.attachments.map((attachment) => (
            <div key={attachment.id} className="flex min-w-0 items-center gap-2">
              <Paperclip
                size={16}
                className="shrink-0 text-chat-user-foreground/70"
                aria-hidden="true"
              />
              <span className="min-w-0 truncate" dir="auto">
                {attachment.file.name}
              </span>
              <span className="shrink-0 text-xs tabular-nums text-chat-user-foreground/70">
                {fileSize(attachment.file.size)}
              </span>
            </div>
          ))}
        </div>
        {message.status === "sending" ? (
          <span
            role="status"
            className="inline-flex items-center gap-1.5 text-xs text-chat-user-foreground/70"
          >
            <LoaderCircle
              size={13}
              className="animate-spin motion-reduce:animate-none"
              aria-hidden="true"
            />
            {t`Sending…`}
          </span>
        ) : message.status === "failed" ? (
          <div className="flex flex-wrap items-center gap-2">
            <span
              role="status"
              className="text-xs text-chat-user-foreground/80"
            >{t`Failed to send message`}</span>
            <Button
              type="button"
              variant="ghost"
              className="min-h-10 px-3"
              disabled={retryDisabled}
              onClick={message.retry}
            >
              {t`Retry`}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
